/**
 * SnapshotIdAssigner - Utility for assigning IDs to paragraph snapshots
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for ensuring paragraph snapshots have stable IDs,
 * which is critical for incremental updates and caching.
 */

import type { ParagraphSnapshot } from '../../../core/types/Typesetter';

/**
 * Assign a stable snapshot ID to a paragraph snapshot
 * 
 * Ensures that a paragraph snapshot has a stable ID for caching and incremental updates.
 * If the snapshot already has an ID, it is preserved. Otherwise, a default ID is assigned.
 * 
 * For single-paragraph documents, the default ID is 'para-0'. For multi-paragraph documents,
 * the ID should be provided by the snapshot itself.
 * 
 * @param snapshot - Paragraph snapshot to assign ID to
 * @param defaultId - Default ID to use if snapshot doesn't have one (default: 'para-0')
 * @returns Snapshot with guaranteed ID
 */
export function assignSnapshotId(
  snapshot: ParagraphSnapshot,
  defaultId: string = 'para-0'
): ParagraphSnapshot & { snapshot_id: string } {
  return {
    ...snapshot,
    snapshot_id: snapshot.snapshot_id || defaultId
  };
}

/**
 * Validate that a snapshot has the required structure for caching
 * 
 * Checks if a snapshot has lines and is valid for caching. Used to determine
 * if a snapshot should be cached after pagination.
 * 
 * @param snapshot - Paragraph snapshot to validate
 * @returns True if snapshot is valid for caching
 */
export function isValidSnapshotForCaching(snapshot: ParagraphSnapshot | null | undefined): boolean {
  return !!(
    snapshot &&
    snapshot.lines &&
    Array.isArray(snapshot.lines) &&
    snapshot.lines.length > 0
  );
}

/**
 * Create a snapshot with ID for caching
 * 
 * Validates the snapshot and assigns an ID if valid. Returns null if snapshot
 * is not valid for caching.
 * 
 * @param snapshot - Paragraph snapshot to process
 * @param defaultId - Default ID to use if snapshot doesn't have one (default: 'para-0')
 * @returns Snapshot with ID if valid, null otherwise
 */
export function createSnapshotWithIdForCaching(
  snapshot: ParagraphSnapshot | null | undefined,
  defaultId: string = 'para-0'
): (ParagraphSnapshot & { snapshot_id: string }) | null {
  if (!isValidSnapshotForCaching(snapshot)) {
    return null;
  }
  
  return assignSnapshotId(snapshot!, defaultId);
}


